import { useMemo } from 'react'
import { useDroppable } from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import SortableFieldCard from './SortableFieldCard'
import type { SchemaField } from '../../../types/schede'
import { Plus } from 'lucide-react'

interface BuilderCanvasProps {
  fields: SchemaField[]
  selectedFieldId: string | null
  onSelectField: (id: string | null) => void
  onRemoveField: (id: string) => void
}

export default function BuilderCanvas({
  fields,
  selectedFieldId,
  onSelectField,
  onRemoveField,
}: BuilderCanvasProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'canvas-droppable',
  })

  // Calcola quali campi sono "dentro" una sezione (per indentazione visiva)
  const sectionMembership = useMemo(() => {
    const membership = new Map<string, string | null>()
    let currentSectionId: string | null = null

    for (const field of fields) {
      if (field.type === 'section') {
        currentSectionId = field.id
        membership.set(field.id, null)
      } else if (field.outside_section) {
        // Campo sganciato: esce dalla sezione corrente
        currentSectionId = null
        membership.set(field.id, null)
      } else {
        membership.set(field.id, currentSectionId)
      }
    }
    return membership
  }, [fields])

  return (
    <div
      ref={setNodeRef}
      className="p-6 max-w-3xl mx-auto min-h-[300px]"
    >
      {fields.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-20 rounded-xl border-2 border-dashed transition-colors"
          style={{
            borderColor: isOver ? 'var(--color-primary)' : 'var(--color-border)',
            color: 'var(--color-text-tertiary)',
            backgroundColor: isOver ? 'var(--color-primary-light, rgba(99,102,241,0.05))' : 'transparent',
          }}
        >
          <Plus className="w-10 h-10 mb-3" />
          <p className="text-sm font-medium">Trascina i campi dal pannello a sinistra</p>
          <p className="text-xs mt-1">oppure clicca su un tipo di campo per aggiungerlo</p>
        </div>
      ) : (
        <SortableContext
          items={fields.map(f => f.id)}
          strategy={verticalListSortingStrategy}
        >
          <div
            className="space-y-2 rounded-xl p-2 transition-colors"
            style={{
              backgroundColor: isOver ? 'var(--color-primary-light, rgba(99,102,241,0.05))' : 'transparent',
            }}
          >
            {fields.map((field) => {
              const isInsideSection = sectionMembership.get(field.id) !== null && field.type !== 'section'
              return (
                <div
                  key={field.id}
                  style={isInsideSection ? { marginLeft: '1.5rem' } : undefined}
                >
                  <SortableFieldCard
                    field={field}
                    isSelected={field.id === selectedFieldId}
                    onSelect={() => onSelectField(field.id)}
                    onRemove={() => onRemoveField(field.id)}
                  />
                </div>
              )
            })}
          </div>
        </SortableContext>
      )}
    </div>
  )
}
